
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import faviconApps from './demo/favicon-apps.twig';
import faviconTabs from './demo/favicon-tabs.twig';
import './demo/favicon-apps.scss';
import './demo/favicon-tabs.scss';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Design/Favicons" mdxType="Meta" />
    <h1>{`Favicons`}</h1>
    <p>{`Our `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Favicon"
      }}>{`favicons`}</a>{` represent Cloud Four in browser tabs, bookmarks and home screens. Since 2021 we've followed `}<a parentName="p" {...{
        "href": "https://evilmartians.com/chronicles/how-to-favicon-in-2021-six-files-that-fit-most-needs"
      }}>{`Andrey Sitnik's recommendation`}</a>{` to focus on a handful of key files that cover most use cases.`}</p>
    <h2>{`Browser tabs`}</h2>
    <p><inlineCode parentName="p">{`favicon.ico`}</inlineCode>{` is for older browsers. `}<inlineCode parentName="p">{`icon.svg`}</inlineCode>{` is for newer browsers and is smaller in filesize. Both are sized at 32 square pixels for high-resolution displays, and both feature the same design with a white border so it will be visually offset in different themes and tab states.`}</p>
    <p>{`We also provide `}<inlineCode parentName="p">{`favicon-dev.ico`}</inlineCode>{` and `}<inlineCode parentName="p">{`favicon-dev.svg`}</inlineCode>{` to help differentiate tabs during development.`}</p>
    <Canvas isColumn mdxType="Canvas">
  <Story name="Favicons" parameters={{
        layout: 'fullscreen',
        docs: {
          source: {
            code: `<link rel="icon" href="path/to/favicon.ico" />
<link rel="icon" href="path/to/icon.svg" type="image/svg+xml" />`
          }
        }
      }} mdxType="Story">
    {faviconTabs}
  </Story>
  <Story name="Favicons (Dev)" parameters={{
        layout: 'fullscreen',
        docs: {
          source: {
            code: `<link rel="icon" href="path/to/favicon-dev.ico" />
<link rel="icon" href="path/to/icon-dev.svg" type="image/svg+xml" />`
          }
        }
      }} mdxType="Story">
    {faviconTabs({
          dev: true
        })}
  </Story>
    </Canvas>
    <p>{`Because `}<inlineCode parentName="p">{`favicon.ico`}</inlineCode>{` was historically located at the root of the web server, it's best to make it accessible there (either directly or through a redirect) to satisfy tools like RSS readers and social media apps that may look for it there.`}</p>
    <h2>{`Android`}</h2>
    <p><inlineCode parentName="p">{`icon-192.png`}</inlineCode>{` and `}<inlineCode parentName="p">{`icon-512.png`}</inlineCode>{` are intended for Android. The logo has been sized so that it may be `}<a parentName="p" {...{
        "href": "https://css-tricks.com/maskable-icons-android-adaptive-icons-for-your-pwa/"
      }}>{`maskable`}</a>{` where supported. These must be configured via a `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/Manifest"
      }}>{`web app manifest`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Android" parameters={{
        docs: {
          source: {
            code: `{
  "icons": [
    {
      "src": "path/to/icon-192.png",
      "type": "image/png",
      "sizes": "192x192",
      "purpose": "maskable",
    },
    {
      "src": "path/to/icon-512.png",
      "type": "image/png",
      "sizes": "512x512",
      "purpose": "maskable",
    },
  ]
}`
          }
        }
      }} mdxType="Story">
    {faviconApps}
  </Story>
    </Canvas>
    <h2>{`Apple`}</h2>
    <p>{`Our logo is sized a little larger for iOS/macOS to match the conventions of that platform. Only a single 180-pixel image (`}<inlineCode parentName="p">{`apple-touch-icon.png`}</inlineCode>{`) is necessary.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Apple" parameters={{
        docs: {
          source: {
            code: `<link rel="apple-touch-icon" href="path/to/apple-touch-icon.png">`
          }
        }
      }} mdxType="Story">
    {faviconApps({
          platform: 'apple'
        })}
  </Story>
    </Canvas>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const favicons = assertIsFn(faviconTabs);
favicons.storyName = 'Favicons';
favicons.parameters = { storySource: { source: 'faviconTabs' }, ...{
  layout: 'fullscreen',
  docs: {
    source: {
      code: `<link rel="icon" href="path/to/favicon.ico" />
<link rel="icon" href="path/to/icon.svg" type="image/svg+xml" />`
    }
  }
} };

export const faviconsDev = () => (
          faviconTabs({
  dev: true
})
        );
faviconsDev.storyName = 'Favicons (Dev)';
faviconsDev.parameters = { storySource: { source: 'faviconTabs({\n  dev: true\n})' }, ...{
  layout: 'fullscreen',
  docs: {
    source: {
      code: `<link rel="icon" href="path/to/favicon-dev.ico" />
<link rel="icon" href="path/to/icon-dev.svg" type="image/svg+xml" />`
    }
  }
} };

export const android = assertIsFn(faviconApps);
android.storyName = 'Android';
android.parameters = { storySource: { source: 'faviconApps' }, ...{
  docs: {
    source: {
      code: `{
  "icons": [
    {
      "src": "path/to/icon-192.png",
      "type": "image/png",
      "sizes": "192x192",
      "purpose": "maskable",
    },
    {
      "src": "path/to/icon-512.png",
      "type": "image/png",
      "sizes": "512x512",
      "purpose": "maskable",
    },
  ]
}`
    }
  }
} };

export const apple = () => (
          faviconApps({
  platform: 'apple'
})
        );
apple.storyName = 'Apple';
apple.parameters = { storySource: { source: 'faviconApps({\n  platform: \'apple\'\n})' }, ...{
  docs: {
    source: {
      code: `<link rel="apple-touch-icon" href="path/to/apple-touch-icon.png">`
    }
  }
} };

const componentMeta = { title: 'Design/Favicons', includeStories: ["favicons","faviconsDev","android","apple"],  };

const mdxStoryNameToKey = {"Favicons":"favicons","Favicons (Dev)":"faviconsDev","Android":"android","Apple":"apple"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;